<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import StatusBanner from './components/app/StatusBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { startPushSync } from './helper/pushRecovery';
import { updateAppBadge } from './helper/bonbozBadge';
import { listenForPushReceiptFlush } from './helper/bonbozPushReceipts';
import { applyTheme, hasLocalThemeChoice } from './helper/chuDe';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    StatusBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      reconnectService: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      // BonBoZ: nguồn cho huy hiệu số đỏ trên icon PWA. Xem `bonbozBadge.js`.
      unreadNotificationCount: 'notifications/getUnreadCount',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
    // BonBoZ: giữ huy hiệu trên icon PWA khớp với số chuông trong trang. Chạy cả
    // lần đầu để xoá số cũ mà service worker đặt lúc app còn đóng.
    unreadNotificationCount: {
      immediate: true,
      handler(count) {
        updateAppBadge(count);
      },
    },
    // BonBoZ: chu de mau khung, luu theo tai khoan nen doi may van theo. Chi
    // ap gia tri may chu khi nguoi dung CHUA tu bam tren may nay — sau do
    // phan hoi luu ve cham/nguoc thu tu se keo mau lui lai. Xem `helper/chuDe.js`.
    'uiSettings.bonboz_chu_de': {
      immediate: true,
      handler(themeName) {
        if (!hasLocalThemeChoice()) applyTheme(themeName);
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );

    // BonBoZ: gửi lại xác nhận thông báo đẩy còn kẹt trong service worker, khi
    // app mở và mỗi lần quay lại màn hình. Xem `helper/bonbozPushReceipts.js`.
    this.stopPushReceiptFlush = listenForPushReceiptFlush();
  },
  unmounted() {
    // BonBoZ: xem `listenForPushReceiptFlush` trong `mounted`.
    this.stopPushReceiptFlush?.();
    this.stopPushSync?.();
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      if (locale) {
        this.$root.$i18n.locale = locale;
        // BonBoZ: ghi luon vao <html lang>. `shared/helpers/timeHelper` doc o
        // day de biet dinh dang thoi gian tuong doi theo ngon ngu nao - no nam
        // o `shared/` nen khong import duoc i18n cua dashboard (phu thuoc vong).
        // Kem loi ich san: trinh doc man hinh doc dung ngon ngu.
        document.documentElement.setAttribute('lang', locale);
      }
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const account = this.getAccount(this.currentAccountId);
      const { locale } = account;
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      // BonBoZ: tự khôi phục đăng ký push, báo trạng thái máy và tải tình
      // trạng điện thoại, lúc mở và mỗi lần quay lại app. Xem
      // `helper/pushRecovery.js`. Đổi tài khoản không gắn thêm listener.
      this.stopPushSync ||= startPushSync();
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <!-- BonBoZ: banner "co ban Chatwoot moi" da bo. Ban nay la fork noi bo,
         nang cap la viec cua nguoi deploy chu khong phai cua nhan vien truc
         page - ho khong tu nang cap duoc, banner chi chiem cho va gay hoang
         mang. Khong dung DISPLAY_MANIFEST=false vi no an luon favicon. -->
    <StatusBanner />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}
</style>
